import { NSARouterMakerResult } from "namirasoft-account-react";
import { GuardRailFullRow, GuardRailInputRow, GuardRailRow, GuardRailRuleInputRow } from "namirasoft-inference";
import { NSBox, NSBoxString, NSBoxTextArea, NSColumn, NSRepeater, NSRow } from "namirasoft-site-react";
import { Component, createRef } from "react";
import GuardRailRuleLine from "../components/GuardRailRuleLine";
import InferencerCommonFields from "../components/InferencerCommonFields";
import { InferenceEntities } from "../InferenceEntities";
import { InferenceInfo } from "../InferenceInfo";
import { InferenceLayout } from '../InferenceLayout';
import { InferenceRouterProps } from "../InferenceRouterProps";

interface GuardRailEditPageProps extends NSARouterMakerResult, InferenceRouterProps
{
    isEdit: boolean;
}

export default class GuardRailEditPage extends Component<GuardRailEditPageProps>
{
    NSRepeater_Rules = createRef<NSRepeater<GuardRailRuleLine, GuardRailRuleInputRow>>();
    NSBoxString_Name = createRef<NSBoxString>();
    InferencerCommonFields_Ids = createRef<InferencerCommonFields>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    override render()
    {
        return (
            <InferenceLayout<GuardRailRow, GuardRailInputRow>
                {...this.props}
                action={{ description: this.props.isEdit ? InferenceInfo.guard_rail.edit : InferenceInfo.guard_rail.create }}
                pages={{
                    entity: InferenceEntities.guard_rail(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): GuardRailInputRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let { memory_id, wiper_id, log_group_id } = this.InferencerCommonFields_Ids.current?.getValue() ?? InferencerCommonFields.EMPTY;
                                        let description = this.NSBoxTextArea_Description.current?.getValue() ?? null;
                                        let rules = this.NSRepeater_Rules.current?.getValue() ?? [];

                                        return {
                                            workspace_id: null,
                                            name,
                                            memory_id,
                                            wiper_id,
                                            log_group_id,
                                            description,
                                            rules,
                                            guard_rail_category: [],
                                            guard_rail_field: [],
                                            guard_rail_tag: [],
                                        };
                                    },
                                    setRow: (row: GuardRailRow) =>
                                    {
                                        let full = row as GuardRailFullRow;

                                        this.NSBoxString_Name.current?.setValue(full.name);
                                        this.InferencerCommonFields_Ids.current?.setValue({
                                            memory_id: full.memory_id,
                                            wiper_id: full.wiper_id,
                                            log_group_id: full.log_group_id,
                                        });
                                        this.NSBoxTextArea_Description.current?.setValue(full.description);
                                        this.NSRepeater_Rules.current?.setValue((full.rules ?? []).map(r => ({
                                            type: r.type,
                                            type_condition_condition_id: r.type_condition_condition_id,
                                            type_conditiongroup_condition_group_id: r.type_conditiongroup_condition_group_id,
                                            type_deniedtopic_topics: r.type_deniedtopic_topics,
                                            type_piicustomsensitivepattern_pattern: r.type_piicustomsensitivepattern_pattern,
                                            type_customprompt_prompt: r.type_customprompt_prompt,
                                            type_customprompt_inferencer_type: r.type_customprompt_inferencer_type,
                                            type_customprompt_inferencer_id: r.type_customprompt_inferencer_id,
                                            on_user_prompt: r.on_user_prompt,
                                            on_ai_output: r.on_ai_output,
                                        })));
                                    }
                                },
                                ui: {
                                    extra_tabs: [
                                        {
                                            title: "Common",
                                            clicked: true,
                                            getContent: () => (
                                                <InferencerCommonFields
                                                    ref={this.InferencerCommonFields_Ids}
                                                    {...this.props}
                                                    hide={{ validator: true, budget: true }}
                                                    info_entity="guard-rail"
                                                />
                                            )
                                        },
                                        {
                                            title: "Rules",
                                            clicked: true,
                                            getContent: () => (
                                                <NSRepeater<GuardRailRuleLine, GuardRailRuleInputRow>
                                                    ref={this.NSRepeater_Rules}
                                                    title="Rules"
                                                    required
                                                    min={1}
                                                    createItem={(gIndex: number, lIndex: number, ref: React.RefObject<GuardRailRuleLine>, onChange: () => void, onDelete: undefined | ((index: number) => void)) =>
                                                    {
                                                        return <GuardRailRuleLine
                                                            ref={ref}
                                                            {...this.props}
                                                            key={`GuardRailRuleLine_${gIndex}`}
                                                            index={lIndex}
                                                            deletable={true}
                                                            onChanged={() => { onChange(); }}
                                                            onDelete={() => { onDelete?.(lIndex); }}
                                                        />;
                                                    }}
                                                    isEmpty={function (_, value: GuardRailRuleInputRow | null): boolean
                                                    {
                                                        return !GuardRailRuleLine.IS_DIRTY(value);
                                                    }}
                                                    getValue={function (item: GuardRailRuleLine, checkError: boolean): GuardRailRuleInputRow | null
                                                    {
                                                        return item.getValue(checkError);
                                                    }}
                                                    setValue={function (item: GuardRailRuleLine, _: number, value: GuardRailRuleInputRow | null): void
                                                    {
                                                        if (value)
                                                            item.setValue(value);
                                                    }}
                                                    style={{ width: NSBox.width.triple }}
                                                />
                                            )
                                        }
                                    ]
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSRow>
                        <NSBoxString
                            ref={this.NSBoxString_Name}
                            required
                            title="Name"
                            style={{ width: NSBox.width.double }}
                            info={{ link: "https://namirasoft.com/inference/console-guide/guard-rail/#field-name" }}
                        />
                    </NSRow>
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        title="Description"
                        required={false}
                        style={{ width: NSBox.width.double }}
                        info={{ link: "https://namirasoft.com/inference/console-guide/guard-rail/#field-description" }}
                    />
                </NSColumn>
            </InferenceLayout>
        );
    }
}